import { useClerk, useUser } from "@clerk/clerk-react";
import { Button } from "@growth/ui";
import { DoorClosed, LoaderCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { FullPageMessage } from "../components/full-page-message";
import { useRefreshMe } from "../lib/me";

/** How often the page looks by itself, so it lets the person in soon after they're added. */
const CHECK_EVERY_MS = 30_000;

/**
 * Signed in, but no workspace has added this person yet. The page keeps checking, and once
 * someone adds them the fresh answer moves them on into the app.
 */
export const NoWorkspacePage = () => {
  const { signOut } = useClerk();
  const { user } = useUser();
  const email = user?.primaryEmailAddress?.emailAddress;
  const refresh = useRefreshMe();
  const { mutate } = refresh;
  const [askedNow, setAskedNow] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (!document.hidden) mutate();
    }, CHECK_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [mutate]);

  const checkNow = () => {
    setAskedNow(true);
    mutate();
  };

  return (
    <FullPageMessage icon={<DoorClosed className="size-5" />} title="You're not in a workspace yet">
      <p>
        {email ? <strong className="text-ink">{email}</strong> : "This account"} hasn't been added
        to a Growth Engine workspace. Ask the person who invited you to add this exact email on
        their Members page. You'll go straight in once they do.
      </p>
      <div className="flex flex-col gap-2">
        <Button onClick={checkNow} disabled={refresh.isPending}>
          {refresh.isPending ? (
            <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="size-4" aria-hidden="true" />
          )}
          Check now
        </Button>
        <Button variant="secondary" onClick={() => signOut({ redirectUrl: "/sign-in" })}>
          Sign in with another account
        </Button>
      </div>
      {askedNow && refresh.isSuccess && (
        <p role="status">Not yet. Nothing has been added for this email so far.</p>
      )}
      {askedNow && refresh.isError && (
        <p role="status">Couldn't check just now. Try again in a moment.</p>
      )}
    </FullPageMessage>
  );
};
